import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { SERVER_URL } from '../databucket';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function headers() {
  const email = localStorage.getItem('outlookUserEmail') || '';
  return {
    'Content-Type': 'application/json',
    ...(email ? { 'x-user-email': email } : {}),
  };
}

const BASE = `${SERVER_URL}/api/mail/scheduled`;

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchScheduledEmails = createAsyncThunk(
  'scheduledMail/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const email = localStorage.getItem('outlookUserEmail') || '';
      const res   = await axios.get(BASE, { params: { email }, headers: headers(), withCredentials: true });
      return res.data.scheduled;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || err.message);
    }
  }
);

export const createScheduledEmail = createAsyncThunk(
  'scheduledMail/create',
  async ({ userEmail, draftId, scheduledAt, subject, to, cc, bcc }, { rejectWithValue }) => {
    try {
      const res = await axios.post(BASE, { userEmail, draftId, scheduledAt, subject, to, cc, bcc }, { headers: headers(), withCredentials: true });
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || err.message);
    }
  }
);

export const rescheduleEmail = createAsyncThunk(
  'scheduledMail/reschedule',
  async ({ id, scheduledAt, draftId }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE}/${id}`, { scheduledAt, draftId }, { headers: headers(), withCredentials: true });
      return { id, ...res.data };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || err.message);
    }
  }
);

export const cancelScheduledEmail = createAsyncThunk(
  'scheduledMail/cancel',
  async ({ id }, { rejectWithValue }) => {
    try {
      const email = localStorage.getItem('outlookUserEmail') || '';
      await axios.delete(`${BASE}/${id}`, { params: { email }, headers: headers(), withCredentials: true });
      return { id };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.error || err.message);
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const scheduledMailSlice = createSlice({
  name: 'scheduledMail',
  initialState: {
    items:  [],        // array of scheduled email records
    status: 'idle',    // idle | loading | loaded | error
    error:  null,
  },
  reducers: {
    addScheduledItem(state, action) {
      state.items.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScheduledEmails.pending,  (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchScheduledEmails.fulfilled,(state, action) => { state.status = 'loaded'; state.items = action.payload; })
      .addCase(fetchScheduledEmails.rejected, (state, action) => { state.status = 'error';  state.error = action.payload; })

      .addCase(cancelScheduledEmail.fulfilled, (state, action) => {
        const item = state.items.find(i => i.id === action.payload.id);
        if (item) item.status = 'cancelled';
      })

      .addCase(rescheduleEmail.fulfilled, (state, action) => {
        const item = state.items.find(i => i.id === action.payload.id);
        if (item && action.payload.scheduledAt) item.scheduledAt = action.payload.scheduledAt;
      });
  },
});

export const { addScheduledItem } = scheduledMailSlice.actions;
export default scheduledMailSlice.reducer;

// Selectors
export const selectScheduledItems  = (state) => state.scheduledMail?.items  || [];
export const selectScheduledStatus = (state) => state.scheduledMail?.status || 'idle';
